'use client'

import { useEffect, useState } from 'react'

import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import type { TopNavV6MiniMenu, TopNavV6MiniMenuItem } from '@src/components/TopNavV6/TopNavV6Props'
import { cn } from '@src/utils/tailwind'

interface TopNavV6DesktopMiniMenuProps {
  menu: TopNavV6MiniMenu
  isVisible: boolean
  onMouseLeave?: () => void
}

const MiniMenuItem = ({ item }: { item: TopNavV6MiniMenuItem }) => {
  const content = (
    <div className="flex items-center gap-1 whitespace-nowrap">
      <span className="font-sans text-body-sm leading-5 text-bodi-gray-butters transition-colors group-hover:text-bodi-white group-hover:underline group-hover:decoration-bodi-white">
        {item.title}
      </span>
      {item.badge && (
        <div className="flex items-center gap-1 text-body-sm leading-5">
          <span className="font-sans font-normal text-bodi-gray-butters">-</span>
          <span className="text-center font-sans font-medium text-bodi-green-bright-500">
            {item.badge}
          </span>
        </div>
      )}
    </div>
  )

  if (item.href) {
    return (
      <LocalizedLink
        href={item.href}
        onClick={item.onClick}
        className="group flex items-center rounded-md transition-colors"
      >
        {content}
      </LocalizedLink>
    )
  }

  return (
    <div
      className="group flex items-center rounded-md transition-colors"
      onClick={item.onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          item.onClick?.()
        }
      }}
      role="button"
      tabIndex={0}
    >
      {content}
    </div>
  )
}

export const TopNavV6DesktopMiniMenu = ({
  menu,
  isVisible,
  onMouseLeave,
}: TopNavV6DesktopMiniMenuProps) => {
  const [shouldShow, setShouldShow] = useState(false)

  useEffect(() => {
    if (isVisible) {
      // Use requestAnimationFrame to ensure the component is rendered in hidden state first
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setShouldShow(true)
        })
      })
    } else {
      setShouldShow(false)
    }
  }, [isVisible])

  return (
    <div
      onMouseLeave={onMouseLeave}
      className={cn(
        'absolute top-full left-0 z-30 -mt-1 w-auto min-w-[200px] border-b border-bodi-gray-dark bg-bodi-black-text py-4 transition-opacity duration-300 ease-in-out',
        {
          'opacity-0 invisible pointer-events-none': !shouldShow,
          'opacity-100 visible': shouldShow,
        }
      )}
      style={{
        backgroundImage:
          'linear-gradient(90deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%), linear-gradient(90deg, rgba(22, 24, 25, 1) 0%, rgba(22, 24, 25, 1) 100%)',
      }}
    >
      <div className="px-4">
        <div className="flex flex-col gap-1">
          {menu.items.map((item, itemIndex) => (
            <MiniMenuItem key={`mini-menu-item-${itemIndex}`} item={item} />
          ))}
        </div>
      </div>
    </div>
  )
}
